import { NgIconComponent } from "@ng-icons/core";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from "@angular/core";
import { CommonModule, DOCUMENT } from "@angular/common";
import { EvaModalLayer, nextModalZIndex, registerModal, unregisterModal } from "../eva-dialog/eva-modal-stack";
import { lockBodyScroll, unlockBodyScroll } from "../styles/body-scroll-lock";

/**
 * EVA Drawer - Composant 100% custom
 *
 * Panneau lateral glissant (sidebar overlay).
 * Utilise uniquement les tokens EVA.
 *
 * Fonctionnements clés :
 * - S'insère automatiquement au niveau du `<body>` pour éviter les problèmes
 *   de containment CSS (position: fixed dans overflow: auto).
 * - Z-index pris dans la pile de modales partagée avec eva-dialog : un drawer
 *   ouvert après une dialog passe au-dessus, et inversement.
 * - Escape ferme uniquement la dialog ou le drawer au sommet de la pile.
 * - `onShow` / `onHide` sont aussi émis quand le parent change `[visible]`.
 *
 * @example
 * // Simple
 * <eva-drawer [(visible)]="isOpen">Contenu</eva-drawer>
 *
 * // Position droite
 * <eva-drawer [(visible)]="isOpen" position="right">Contenu</eva-drawer>
 *
 * // Avec header
 * <eva-drawer [(visible)]="isOpen" header="Titre du panneau">Contenu</eva-drawer>
 */
@Component({
  selector: "eva-drawer",
  templateUrl: "./eva-drawer.component.html",
  styleUrls: ["./eva-drawer.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgIconComponent, CommonModule],
})
export class EvaDrawerComponent implements OnChanges, AfterViewInit, EvaModalLayer {
  private el = inject(ElementRef);
  private doc = inject(DOCUMENT);

  // ==========================================================================
  // Inputs
  // ==========================================================================

  /** Visibilité du drawer */
  @Input() visible: boolean = false;

  /** Position */
  @Input() position: "left" | "right" | "top" | "bottom" = "right";

  /** Titre du header */
  @Input() header?: string;

  /** Sous-titre du header */
  @Input() subtitle?: string;

  /** Afficher le bouton fermer */
  @Input() showClose: boolean = true;

  /** Fermer au clic sur le backdrop */
  @Input() dismissible: boolean = true;

  /** Afficher le backdrop */
  @Input() modal: boolean = true;

  /** Largeur (pour left/right) */
  @Input() width: string = "400px";

  /** Hauteur (pour top/bottom) */
  @Input() height: string = "300px";

  /** Bloquer le scroll du body */
  @Input() blockScroll: boolean = true;

  /** Classe CSS personnalisée */
  @Input() styleClass: string = "";

  // ==========================================================================
  // Outputs
  // ==========================================================================

  /** Émis quand la visibilité change — pour le two-way binding [(visible)] */
  @Output() visibleChange = new EventEmitter<boolean>();
  /** Émis quand le drawer s'ouvre */
  @Output() onShow = new EventEmitter<void>();
  /** Émis quand le drawer se ferme */
  @Output() onHide = new EventEmitter<void>();

  // ==========================================================================
  // State
  // ==========================================================================

  private isAnimating: boolean = false;

  private isOpen: boolean = false;

  /** Z-index courant de cette instance (assigné à l'ouverture) */
  currentZIndex: number = 1000;

  constructor(private cdr: ChangeDetectorRef) {
    inject(DestroyRef).onDestroy(() => {
      this.unregisterOpen();
      const el = this.el.nativeElement;
      if (el.parentNode) {
        el.parentNode.removeChild(el);
      }
    });
  }

  // ==========================================================================
  // Lifecycle
  // ==========================================================================

  ngAfterViewInit(): void {
    this.doc.body.appendChild(this.el.nativeElement);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const visibleChange = changes["visible"];
    if (!visibleChange) return;
    // Émission différée : on est dans la détection du parent, un handler qui le modifie ferait NG0100.
    if (visibleChange.currentValue) {
      this.handleOpen(true);
    } else {
      this.handleClose(true);
    }
  }

  // ==========================================================================
  // Methods
  // ==========================================================================

  /** Appelé pour le drawer au sommet de la pile quand l'utilisateur presse Escape. */
  closeFromEscape(): void {
    if (this.visible && this.dismissible) {
      this.hide();
    }
  }

  show(): void {
    if (this.visible || this.isAnimating) return;

    this.doc.body.appendChild(this.el.nativeElement);

    this.visible = true;
    this.visibleChange.emit(true);
    this.handleOpen();

    this.cdr.markForCheck();
  }

  hide(): void {
    if (!this.visible || this.isAnimating) return;

    this.isAnimating = true;
    this.visible = false;
    this.visibleChange.emit(false);
    this.handleClose();

    setTimeout(() => {
      this.isAnimating = false;
      this.cdr.markForCheck();
    }, 300);

    this.cdr.markForCheck();
  }

  toggle(): void {
    if (this.visible) {
      this.hide();
    } else {
      this.show();
    }
  }

  onBackdropClick(): void {
    if (this.dismissible) {
      this.hide();
    }
  }

  private handleOpen(deferEmit = false): void {
    if (this.isOpen) return;
    this.isOpen = true;
    this.currentZIndex = nextModalZIndex(this, this.el.nativeElement, this.doc);
    registerModal(this, this.doc);
    if (this.blockScroll) lockBodyScroll(this);
    this.emit(this.onShow, deferEmit);
    this.cdr.markForCheck();
  }

  private handleClose(deferEmit = false): void {
    if (!this.isOpen) return;
    this.unregisterOpen();
    this.emit(this.onHide, deferEmit);
  }

  private emit(emitter: EventEmitter<void>, defer: boolean): void {
    if (defer) {
      Promise.resolve().then(() => emitter.emit());
    } else {
      emitter.emit();
    }
  }

  private unregisterOpen(): void {
    this.isOpen = false;
    unlockBodyScroll(this);
    unregisterModal(this);
  }

  get drawerStyle(): { [key: string]: string } {
    if (this.position === "left" || this.position === "right") {
      return { width: this.width };
    }
    return { height: this.height };
  }
}
